import type { PlanningValueObjectsPeriodRoleEnum } from 'generated/resources/graphql'

export type PeriodRoleEntry = {
  role: PlanningValueObjectsPeriodRoleEnum
  planningIds?: string[] | null
}

export type PeriodRoleContext = {
  higherRole: PlanningValueObjectsPeriodRoleEnum
  roles: PeriodRoleEntry[]
}

export type PlanningCapability =
  | 'editPeriod'
  | 'deletePeriod'
  | 'manageCollaborators'
  | 'viewCollaborators'
  | 'managePlanners'
  | 'viewPlanners'
  | 'createRequest'
  | 'createRequestIfPeriodActive'
  | 'editRequest'
  | 'approveRejectRequest'
  | 'manageSubmissionPeriods'
  | 'viewSubmissionPeriods'

/**
 * Capabilities per role.
 * `createRequest` → can create in any period status (draft, scheduled, active).
 * `createRequestIfPeriodActive` → can only create when the period is active.
 *    Also implies the user can edit/delete their own requests.
 */

const ROLE_CAPABILITIES: Record<PlanningValueObjectsPeriodRoleEnum, readonly PlanningCapability[]> =
  {
    creator: [
      'editPeriod',
      'deletePeriod',
      'manageCollaborators',
      'viewCollaborators',
      'managePlanners',
      'viewPlanners',
      'createRequest',
      'editRequest',
      'manageSubmissionPeriods',
      'viewSubmissionPeriods',
    ],
    approver: ['createRequest', 'approveRejectRequest', 'viewSubmissionPeriods'],
    owner: [
      'editPeriod',
      'deletePeriod',
      'manageCollaborators',
      'viewCollaborators',
      'managePlanners',
      'viewPlanners',
      'createRequest',
      'editRequest',
      'manageSubmissionPeriods',
      'viewSubmissionPeriods',
    ],
    global_editor: [
      'editPeriod',
      'manageCollaborators',
      'viewCollaborators',
      'managePlanners',
      'viewPlanners',
      'createRequest',
      'editRequest',
      'manageSubmissionPeriods',
      'viewSubmissionPeriods',
    ],
    global_viewer: ['viewCollaborators', 'viewSubmissionPeriods', 'viewPlanners'],
    editor: [
      'manageCollaborators',
      'viewCollaborators',
      'createRequestIfPeriodActive',
      'editRequest',
      'viewSubmissionPeriods',
    ],
    viewer: ['viewSubmissionPeriods'],
    // planner: can create requests only when the period is active (no bypass for draft/scheduled)
    planner: ['createRequestIfPeriodActive', 'viewSubmissionPeriods'],
  }

/**
 * Returns the union of capabilities for all provided roles.
 * When a user has multiple roles, "yes" (having a capability) always wins over "no".
 */
export function getCapabilities(roles: PeriodRoleEntry[]): Set<PlanningCapability> {
  const capabilities = new Set<PlanningCapability>()
  for (const roleEntry of roles) {
    const roleCaps = ROLE_CAPABILITIES[roleEntry.role] ?? []
    for (const cap of roleCaps) {
      capabilities.add(cap)
    }
  }
  return capabilities
}
